import { Label } from "layout/typography/Label";
import { SelectButton } from "primereact/selectbutton";
import { BenefitTierPreference } from "types/BenefitSelection/BenefitSelection";

import { TIER_CHOICES } from "./constants";

interface ITierPreferenceControlProps {
  /** Also the control's `data-testid`, so the two placements are told apart in a test. */
  id: string;
  value: BenefitTierPreference;
  disabled?: boolean;
  onChange: (value: BenefitTierPreference) => void;
}

/**
 * CLE-2792 — where this facility stands in a payer's tiered network.
 *
 * 🚨 IT ASKS ABOUT THE FACILITY, NOT ABOUT THE ENGINE. The control used to be labelled "tier to
 * read when the payer tiers a plan", which required the reader to know that a tier is a step in a
 * selection chain before they could answer it. A tier is a fact about this facility's standing with
 * the payer — measured over 90 days of stored responses, United Healthcare is the only payer that
 * sends per-tier amounts, and its `P - TIER 1` (sometimes naming a health system, `P - TIER 1
 * MERCY`) is the designated/preferred provider with the lowest cost share while `2 - TIER 2` is an
 * ordinary in-network one. Tier 3 and Tier 4 are out of network and are never read. So the question
 * the facility's staff can actually answer is which of the two this facility is.
 *
 * 🚨 THE VALUE WAS ALWAYS ON THE ROW AND THE SCREEN NEVER OFFERED IT. `AccountBenefitMap
 * .tierPreference` is on every map row and the `PUT` has accepted it on the service line and on
 * each exception since the endpoint was written, but nothing here could set either one: an account
 * whose payer tiers its plans read `Tier2` because that is what the seeder wrote, and changing it
 * meant an UPDATE by hand.
 *
 * ⚠️ THE HELPER LINE NO LONGER PROMISES THAT A PAYER FILTER'S TIER WINS. It never did for the
 * engine — the filter narrowed the lines to Tier 1, the engine's own step then looked for Tier 2,
 * found nothing and fell to the summary — and the tier has since left the payer filter entirely.
 * This row is now the only place a tier is set.
 *
 * `SelectButton`, matching the tie-break control in `LineSelectionDialog`: two mutually exclusive
 * values, both worth showing at once. `e.value` is null when somebody clicks the selected segment,
 * and that is ignored — a service line is always read at one tier or the other.
 */
export function TierPreferenceControl({
  id,
  value,
  disabled,
  onChange,
}: ITierPreferenceControlProps) {
  return (
    <div className="flex flex-column gap-2" data-testid={id}>
      <Label htmlFor={id}>
        When a payer tiers its network, this facility is
      </Label>
      <SelectButton
        id={id}
        data-testid={`${id}.select`}
        options={TIER_CHOICES}
        optionLabel="label"
        optionValue="value"
        value={value}
        disabled={disabled}
        onChange={(e) => e.value && onChange(e.value)}
      />
      <span className="text-xs text-color-secondary">
        Only United Healthcare sends tiered benefits today. Tier 3 and above are
        out of network and never read.
      </span>
    </div>
  );
}
